/**
 * Email gate shown to clients before a gated public proposal opens.
 *
 * Why a gate?
 * Staff (you / Nicki / Arooj) list the recipient emails when minting
 * a share link. The backend extracts the work domain(s) and only lets
 * emails from those domains through. Result: a real audit trail of
 * WHO is opening the proposal (named recipients + forwards within
 * the same company), and no leakage to personal / competitor inboxes.
 *
 * Behaviour
 * - The Public viewer mounts this whenever GET /gate/status returns
 *   `required: true && satisfied: false`.
 * - On success the backend sets an HTTP-only cookie that valid for
 *   14 days, so repeat visits skip the gate entirely.
 * - Blocked attempts (wrong domain / personal email) are quietly
 *   logged on the server so staff get a forensic record.
 *
 * Layout: centered card on a neutral page background, matches the
 * proposal-loading screen aesthetic so the transition feels native.
 */
import { useState } from "react";
import axios from "axios";
import { Lock, ShieldAlert, Mail, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const REASON_COPY = {
  // Backend reason codes → client-friendly copy. We deliberately
  // don't reveal the allow-list ("which domains would work") to
  // avoid leaking the client's identity to anyone probing the URL.
  invalid_email: {
    title: "That doesn't look like a valid email",
    body: "Please enter a work email address.",
  },
  personal_domain: {
    title: "Please use your work email",
    body: "Personal email accounts (Gmail, Yahoo, Outlook, etc.) aren't permitted for this proposal. Use the corporate address your Medicys contact emailed.",
  },
  not_allowed: {
    title: "Not on the access list",
    body: "This email isn't authorised to view this proposal. If you believe this is in error, please reach out to your Medicys contact.",
  },
  not_configured: {
    title: "Access is being set up",
    body: "This proposal hasn't been opened to recipients yet. Please contact your Medicys representative to be added to the access list.",
  },
};


export default function ShareGate({ token, mode, onPass }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError({ title: "Email required", body: "Please enter your work email." });
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { data } = await axios.post(
        `${API}/api/public/proposals/${encodeURIComponent(token)}/gate`,
        { email: email.trim() },
        { withCredentials: true },
      );
      onPass?.(data.as_email);
    } catch (err) {
      const reason = err?.response?.data?.detail?.reason || "not_allowed";
      const copy = REASON_COPY[reason] || REASON_COPY.not_allowed;
      setError(copy);
    } finally {
      setBusy(false);
    }
  };

  // Pre-configured links (mode = pending_config) show a different
  // gate that doesn't even offer the email input - there's no
  // domain to validate against yet, so we just tell the visitor to
  // contact their Medicys rep.
  const isPendingConfig = mode === "pending_config";

  return (
    <div
      data-testid="share-gate"
      className="min-h-screen bg-stone-50 flex items-center justify-center px-4 py-10"
    >
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-7 sm:p-9">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-7 h-7 rounded-full bg-[#1B4F8A]/10 flex items-center justify-center">
              <Lock className="w-3.5 h-3.5 text-[#1B4F8A]" />
            </div>
            <p className="text-[10px] font-mono tracking-[0.22em] uppercase text-[#1B4F8A]">
              Secure proposal access
            </p>
          </div>
          <h1 className="font-serif text-2xl sm:text-3xl font-light text-slate-900 leading-tight mt-3">
            {isPendingConfig
              ? "This proposal is being set up"
              : "Verify your work email"}
          </h1>
          {isPendingConfig ? (
            <p className="text-sm text-stone-600 mt-3 leading-relaxed">
              Access to this proposal hasn&apos;t been opened to recipients yet.
              Please contact your Medicys representative to be added to the
              access list.
            </p>
          ) : (
            <p className="text-sm text-stone-600 mt-3 leading-relaxed">
              For confidentiality, this proposal opens only for
              invited work-email addresses. Enter your work email
              to continue. We&apos;ll remember this device for
              14 days.
            </p>
          )}

          {!isPendingConfig && (
            <form onSubmit={submit} className="mt-6 space-y-3">
              <label
                htmlFor="gate-email"
                className="block text-[10px] font-mono uppercase tracking-wider text-stone-500"
              >
                Work email
              </label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none" />
                <input
                  id="gate-email"
                  data-testid="share-gate-email-input"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  autoFocus
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@company.com"
                  className="w-full pl-9 pr-3 py-2.5 rounded-md border border-stone-300 bg-white text-sm text-slate-900 focus:outline-none focus:border-[#1B4F8A] focus:ring-1 focus:ring-[#1B4F8A] disabled:opacity-50"
                  disabled={busy}
                />
              </div>
              {error && (
                <div
                  data-testid="share-gate-error"
                  className="flex items-start gap-2 px-3 py-2 rounded-md border border-rose-200 bg-rose-50 text-rose-800 text-xs"
                >
                  <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium">{error.title}</p>
                    <p className="text-rose-700 mt-0.5 leading-relaxed">{error.body}</p>
                  </div>
                </div>
              )}
              <button
                type="submit"
                disabled={busy}
                data-testid="share-gate-submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#1B4F8A] text-white px-4 py-2.5 rounded-md text-sm font-medium hover:bg-[#163e6e] disabled:opacity-50 transition-colors"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                {busy ? "Verifying…" : "Continue to proposal"}
              </button>
              <p
                data-testid="share-gate-consent-note"
                className="text-[11px] text-stone-500 leading-relaxed pt-1"
              >
                To tailor our follow-up, Medicys keeps a record of how this
                proposal is viewed, including pages read, downloads and
                figures adjusted, linked to your email. By continuing you
                consent to this.
              </p>
            </form>
          )}
        </div>
        <p className="text-[10px] font-mono uppercase tracking-wider text-stone-400 mt-4 text-center">
          Medicys · Confidential - for invited recipients only
        </p>
      </div>
    </div>
  );
}
